import { CSS } from "@dnd-kit/utilities";
import {
  MouseSensor as LibMouseSensor,
  KeyboardSensor,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { faL, faXmark } from "@fortawesome/free-solid-svg-icons";
import warning from "./warning.png";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";
import PageTemplate from "../Templates/PageTemplate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPenToSquare,
  faCaretLeft,
  faGear,
} from "@fortawesome/free-solid-svg-icons";
import TablePreset from "../Table/TablePreset";
import ToastError from "../Toasts/ToastError";
import ToastSuccess from "../Toasts/ToastSuccess";
import ToastWarning from "../Toasts/ToastWarning";
import middleware from "../Api/Middleware";
import Cookies from "universal-cookie";
import { useParams, useNavigate } from "react-router";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

function shouldHandleEvent(element) {
  let cur = element;

  while (cur) {
    if (cur.dataset && cur.dataset.noDnd) {
      return false;
    }
    cur = cur.parentElement;
  }
  return true;
}

class MouseSensor extends LibMouseSensor {
  static activators = [
    {
      eventName: "onMouseDown",
      handler: ({ nativeEvent: event }) => {
        return shouldHandleEvent(event.target);
      },
    },
  ];
}
function Grid({ children, columns }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridGap: 10,
        padding: 10,
      }}
      className="GridMediaUploader"
    >
      {children}
    </div>
  );
}


function base64ToBlob(base64, sliceSize = 512) {
  const byteCharacters = atob(base64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays);
  return blob;
}

// This is used to render the photo's. For the actual styling and format of the 
//  draggable you need to look at the [Photo] function
const SortablePhoto = (props) => {
  //download image

  const sortable = useSortable({ id: props.position });
  const { attributes, listeners, setNodeRef, transform, transition } = sortable;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Photo
      style={style}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      {...props}
    />
  );
};

// This is used to render the image draggable. This contains the styling and the html in order to render the photo.
const Photo = forwardRef(
  (
    {
      error,
      required,
      index,
      variationIndex,
      file,
      colorId,
      sizeId,
      url,
      name,
      handleVariationChange,
      handleDelete,
      useColors,
      variationOptions,
      variationSwitch,
      maxVariations,
      ...props
    },
    ref
  ) => {
    // For debugging
    // console.log("Photo rendering with variation options:", variationOptions);
    // console.log("Current variationIndex:", variationIndex);
    // console.log("variationSwitch:", variationSwitch);
    // console.log("maxVariations:", maxVariations);

    // Filter variation options to get unique ones
    let uniqueVariationOptions = [];

    if (variationOptions && Array.isArray(variationOptions)) {
      // Create a map to track unique values
      const uniqueMap = new Map();

      // Process all variation options to uniquely identify them
      variationOptions.forEach(variation => {
        if (variationSwitch) {
          // For sizes
          const sizeId = variation.size_id;
          const sizeName = variation.size_name;

          if (sizeId && sizeName && !uniqueMap.has(sizeId)) {
            uniqueMap.set(sizeId, {
              ...variation,
              value: sizeId,
              name: sizeName
            });
          }
        } else {
          // For colors
          const colorId = variation.color_id;
          const colorName = variation.color_name;

          if (colorId && colorName && !uniqueMap.has(colorId)) {
            uniqueMap.set(colorId, {
              ...variation,
              value: colorId,
              name: colorName
            });
          }
        }
      });

      // Convert the map values to an array
      uniqueVariationOptions = Array.from(uniqueMap.values());
    }

    //Return the image div
    return (
      <div ref={ref} {...props} className="objectWithinMU">
        <div className={error ? "errorImageBoxMU imageBoxMU" : "imageBoxMU"}>
          {/* If the file exists and it's under 10mb it will render it normally */}
          {file && file.size < MAX_FILE_SIZE ? (
            <>
              <img
                className="col-12 imageInObjectMU p-3"
                src={url}
                draggable="false"
              />
              <div className="imageInformationContainerMU text-break">
                {/* Image size */}
                <b className="imageNameInObjectMU fw-medium" id="imageSize">
                  {file.size.toString().length >= 6 ? (
                    <span style={{ color: "#6a6a6a" }}>
                      {(file.size / 1024 / 1000).toFixed(1) + " MB"}{" "}
                    </span>
                  ) : (
                    <span style={{ color: "#6a6a6a" }}>
                      {(file.size / 1024).toFixed(1) + " kB"}{" "}
                    </span>
                  )}
                </b>

                {/* File name */}
                <div
                  className="imageNameInObjectMU p-2"
                  id="imageName"
                  data-bs-toggle="tooltip"
                  data-bs-placement="bottom"
                  title={name}
                >
                  <span style={{ color: "#6a6a6a" }}>{name}</span>
                </div>

                {/* Delete button */}
                <div
                  data-no-dnd="true"
                  className="imageNameInObjectMU"
                  id="deleteButton"
                >
                  <button
                    type="submit"
                    className="position-absolute d-flex justify-center px-1 deleteImageButtonMU"
                    onClick={() => handleDelete(index)}
                  >
                    <FontAwesomeIcon icon={faXmark} className="deleteIconMU" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            // This renders the error image and styling
            <>
              <img
                className="col-12 imageInObjectMU"
                src={warning}
                draggable="false"
              />
              <div className="imageInformationContainerMU text-break">
                {/* File name */}
                <div className="imageNameInObjectMU px-2" id="imageName">
                  <span>{name}</span>
                </div>

                {/* Delete button */}
                <div
                  data-no-dnd="true"
                  className="imageNameInObjectMU"
                  id="deleteButton"
                >
                  <button
                    type="submit"
                    className="position-absolute d-flex justify-center px-1 deleteImageButtonMU"
                    onClick={() => handleDelete(index)}
                  >
                    <FontAwesomeIcon icon={faXmark} className="deleteIconMU" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* The select object for the color/size options */}
        {(useColors ?? false) && (
          <div data-no-dnd="true">
            <select
              className="mb-3 py-1 col-12 standardInput standardDropdownArrow"
              value={variationIndex || ""}
              disabled={!uniqueVariationOptions.length}
              id={variationIndex === undefined && required ? "required" : ""}
              onChange={(event) => {
                handleVariationChange(index, event.target.value);
              }}
            >
              <option value="">
                {uniqueVariationOptions.length
                  ? variationSwitch
                    ? "Link maat"
                    : "Link kleur"
                  : "Geen " + (variationSwitch ? "maten" : "kleuren")}
              </option>
              {uniqueVariationOptions.map((variation, idx) => {
                const optionValue = variation.value;
                const optionName = variation.name;
                const isDisabled = maxVariations.includes(Number(optionValue));

                return (
                  <option
                    key={idx}
                    value={optionValue}
                    disabled={isDisabled}
                  >
                    {optionName}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>
    );
  }
);

const MediaUploaderComponent = forwardRef(
  (
    {
      useColors,
      availableVariations,
      edit,
      updatedMedia,
      setUpdatedMedia,
      setImages,
      images,
      setCheck,
      product,
      variationSwitch,
      setVariationSwitch,
    },
    ref
  ) => {
    const sensors = useSensors(
      useSensor(MouseSensor),
      useSensor(TouchSensor),
      useSensor(KeyboardSensor)
    );
    const { product_id } = useParams();
    const fileInputRef = useRef(null);
    const hasFetchedImages = useRef(false);
    const [mouseDownX, setMouseDownX] = useState(null);
    const [mouseDownY, setMouseDownY] = useState(null);
    const [requiredCheck, setRequiredCheck] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadCount, setDownloadCount] = useState(0);
    const [uploadCount, setUploadCount] = useState(0);
    const [uploadImgStatus, setUploadImgStatus] = useState("");
    const [variation, setVariation] = useState([]);
    const [variations, setVariations] = useState([]);
    const [variationsLoaded, setVariationsLoaded] = useState(false);
    const [switchDisabled, setSwitchDisabled] = useState(false);
    const [selectedColor, setSelectedColor] = useState("");
    const [selectedSize, setSelectedSize] = useState(null);
    const [uniqueOptions, setUniqueOptions] = useState([]);
    const [maxVariations, setMaxVariations] = useState([]);

    const allowedImageFormats = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/jpeg",
      "image/svg",
      "image/webp",
    ];

    // Check if variations contain sizes or colors
    function checkVariationTypes() {
      let hasColors = false;
      let hasSizes = false;

      if (availableVariations) {
        availableVariations.forEach(variation => {
          if (variation.colors && variation.colors.length > 0) {
            hasColors = true;
          }
          if (variation.sizes && variation.sizes.length > 0) {
            hasSizes = true;
          }
        });
      }

      return { hasColors, hasSizes };
    }

    // Track images per variation to enforce the 3-image limit
    useEffect(() => {
      const newMaxVariations = [];

      if (uniqueOptions.length > 0 && images.length > 0) {
        uniqueOptions.forEach(variation => {
          let id = null;

            setSwitchDisabled(true);
          if (variationSwitch && variation.sizes && variation.sizes.length > 0) {
            id = variation.sizes[0].size_id;
          } else if (!variationSwitch && variation.colors && variation.colors.length > 0) {
            id = variation.colors[0].color_id;
          }

          if (id !== null) {
            const imagesWithThisVariation = images.filter(
              img => Number(variationSwitch ? img.sizeId : img.colorId) === Number(id)
            );

            if (imagesWithThisVariation.length >= 3) {
              newMaxVariations.push(Number(id));
            }
          }
        });
      }else setSwitchDisabled(false)

      setMaxVariations(newMaxVariations);
    }, [images, uniqueOptions, variationSwitch]);

    // Initial setup
    useEffect(() => {
      if (availableVariations) {
        // Check variation types
        const { hasColors, hasSizes } = checkVariationTypes();

        // Fetch images if editing
        if (edit && !hasFetchedImages.current) {
          getImages();
          hasFetchedImages.current = true;
        }
      }
    }, [availableVariations]);

    // Process unique options for dropdown selection
    useEffect(() => {
      if (availableVariations) {
        const newUniqueOptions = [];
        const seenIds = new Set();

        availableVariations.forEach(variation => {
          if (variationSwitch) {
            // Process sizes
            if (variation.sizes && variation.sizes.length > 0) {
              const sizeId = variation.sizes[0].size_id;

              if (sizeId && !seenIds.has(sizeId)) {
                seenIds.add(sizeId);
                newUniqueOptions.push(variation);
              }
            }
          } else {
            // Process colors
            if (variation.colors && variation.colors.length > 0) {
              const colorId = variation.colors[0].color_id;

              if (colorId && !seenIds.has(colorId)) {
                seenIds.add(colorId);
                newUniqueOptions.push(variation);
              }
            }
          }
        });

        setUniqueOptions(newUniqueOptions);
      }
    }, [availableVariations, variationSwitch]);

    // Prepare variation options for the dropdown
    useEffect(() => {
      if (useColors && availableVariations) {
        const processedVariations = [];

        availableVariations.forEach(variation => {
          if (variationSwitch) {
            // Process sizes
            if (variation.sizes && variation.sizes.length > 0) {
              const sizeId = variation.sizes[0].size_id;
              const sizeName = variation.sizes[0].main;

              if (sizeId && sizeName) {
                processedVariations.push({
                  size_id: sizeId,
                  size_name: sizeName,
                  colors: variation.colors || []
                });
              }
            }
          } else {
            // Process colors
            if (variation.colors && variation.colors.length > 0) {
              const colorId = variation.colors[0].color_id;
              const colorName = variation.colors[0].name;

              if (colorId && colorName) {
                processedVariations.push({
                  color_id: colorId,
                  color_name: colorName,
                  sizes: variation.sizes || []
                });
              }
            }
          }
        });

        // Remove duplicates (by id)
        const uniqueVariations = [];
        const seenIds = new Set();

        processedVariations.forEach(variation => {
          const id = variationSwitch ? variation.size_id : variation.color_id;

          if (!seenIds.has(id)) {
            seenIds.add(id);
            uniqueVariations.push(variation);
          }
        });

        setVariation(uniqueVariations);
      }

      setVariationsLoaded(true);
    }, [variationSwitch, availableVariations]);

    // Switch functions
    window.isDeleteOrDragEndTriggered = true;
    window.mouseMoved = true;
    window.clickedOnTheColor = true;

    const handleVariationSwitch = (event) => {
      // Check if the switch isn't disabled
      if (!switchDisabled) {
        const newValue = event.target.value === "true";
        setVariationSwitch(newValue);
        // Reset variation indices when switching
        setImages(
          images.map(img => ({
            ...img,
            variationIndex: "",
            colorId: newValue ? null : img.colorId,
            sizeId: newValue ? img.sizeId : null
          }))
        );
      } else {
        // Show error if trying to switch while disabled
        ToastError(
          "Deze optie kan niet worden veranderd wanneer er één of meerdere afbeeldingen geupload zijn",
          "SizeAndColorError"
        );
      }
    };

    return (
      <div className={useColors ? "whiteBox p-5" : "whiteBox p-5"}>
        <div className="whiteBox mb-1 w-100">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <h5 className="inputTitle p-0 m-0">
                  Afbeelding uploaden
                </h5>
                {useColors &&
                  (variationSwitch ? (
                    <p className="fw-bold p-0 m-0" style={{ fontSize: '14px' }}>
                      (Maximaal 3 afbeeldingen per maat)
                    </p>
                  ) : (
                    <p className="fw-bold p-0 m-0" style={{ fontSize: '14px' }}>
                      (Maximaal 3 afbeeldingen per kleur)
                    </p>
                  ))}
              </div>
              {useColors && (
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex flex-column gap-2">
                    <p className="fw-bold p-0 m-0" style={{ fontSize: '14px' }}>Selecteer maat of kleur</p>
                    <div className="d-flex gap-2">
                      <label className="d-flex align-items-center gap-1 mb-0">
                        <input
                          type="radio"
                          name="variation"
                          className="form-check-input m-0"
                          value={true}
                          checked={variationSwitch === true}
                          onChange={handleVariationSwitch}
                          disabled={switchDisabled}
                        />
                        <span style={{ fontSize: '14px' }}>Maat</span>
                      </label>
                      <label className="d-flex align-items-center gap-1 mb-0">
                        <input
                          type="radio"
                          name="variation"
                          className="form-check-input m-0"
                          value={false}
                          checked={variationSwitch === false}
                          onChange={handleVariationSwitch}
                          disabled={switchDisabled}
                        />
                        <span style={{ fontSize: '14px' }}>Kleur</span>
                      </label>
                    </div>
                  </div>
                  <select
                    onChange={(e) => {
                      const value = e.target.value;
                      if (variationSwitch) {
                        // Set size
                        setSelectedSize(value);
                        setSelectedColor(null);
                      } else {
                        // Set color
                        setSelectedColor(value);
                        setSelectedSize(null);
                      }
                    }}
                    className="standardInput standardDropdownArrow"
                    style={{ minWidth: "200px", maxWidth: "300px", fontSize: '14px' }}
                  >
                    <option value="">
                      {variationSwitch
                        ? uniqueOptions.find(v => v.sizes && v.sizes.length > 0)
                          ? "Selecteer maat"
                          : "Geen maten"
                        : uniqueOptions.find(v => v.colors && v.colors.length > 0)
                          ? "Selecteer kleur"
                          : "Geen kleuren"}
                    </option>
                    {uniqueOptions.map((variation, index) => {
                      const optionValue = variationSwitch
                        ? variation.sizes?.[0]?.size_id
                        : variation.colors?.[0]?.color_id;

                      const optionName = variationSwitch
                        ? variation.sizes?.[0]?.main
                        : variation.colors?.[0]?.name;

                      if (!optionValue || !optionName) {
                        return null;
                      }

                      return (
                        <option key={index} value={optionValue}>
                          {optionName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="fileUploader fileUploaderWidthHeight">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div
              className="mediaUploader"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClick}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
            >
              {uploading || downloading ? (
                <div className="uploadScreenMU">
                  <div className="uploadScreenContainerMU">
                    <div className="spinner-border me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="uploadScreenTextMU">
                      {uploadImgStatus}
                    </span>
                    <span className="uploadScreenTextMU uploadScreenSubTextMU">
                      (
                      {uploading
                        ? `${uploadCount} / ${images.length}`
                        : downloading
                          ? `${uploadCount} / ${downloadCount}`
                          : ""}
                      )
                    </span>
                  </div>
                </div>
              ) : null}
              <input
                id="fileInput"
                ref={fileInputRef}
                hidden
                type="file"
                onChange={handleFileUpload}
                accept={allowedImageFormats.join(",")}
                multiple
              />
              {images.length < 1 && (
                <div className="textForTheMU">
                  Sleep of selecteer uw media-bestanden om te uploaden
                </div>
              )}
              <SortableContext items={images} strategy={rectSortingStrategy}>
                <Grid columns={5}>
                  {variationsLoaded &&
                    images.map(
                      ({ id, file, name, error, url, variationIndex }, index) => (
                        <SortablePhoto
                          key={id ?? index}
                          error={error}
                          required={requiredCheck}
                          file={file}
                          url={url}
                          index={index}
                          useColors={useColors}
                          name={name}
                          variationIndex={variationIndex}
                          handleDelete={handleDelete}
                          variationOptions={variation}
                          handleVariationChange={handleVariationChange}
                          variationSwitch={variationSwitch}
                          position={id}
                          id={id}
                          maxVariations={maxVariations}
                        />
                      )
                    )}
                </Grid>
              </SortableContext>
            </div>
          </DndContext>
        </div>
      </div>
    );

    // Event handlers for drag and drop
    function handleDragEnter(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    function handleDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    function handleVariationChange(index, id) {
      // Convert id to number if it's not empty
      id = id !== "" && id !== null ? Number(id) : id;

      const newImages = [...images];

      if (variationSwitch) {
        // For sizes
        if (id !== null && id !== "") {
          // Check if we already have 3 images with this size
          const imagesWithSize = newImages.filter(
            img => Number(img.sizeId) === id && newImages.indexOf(img) !== index
          );

          if (imagesWithSize.length >= 3) {
            ToastError(
              "Er mogen maar 3 afbeeldingen dezelfde maat hebben",
              "imageVariation"
            );
            return;
          }

          newImages[index].sizeId = id;
          newImages[index].colorId = null; // Clear color ID when setting size
        } else {
          newImages[index].sizeId = id;
        }
      } else {
        // For colors
        if (id !== null && id !== "") {
          // Check if we already have 3 images with this color
          const imagesWithColor = newImages.filter(
            img => Number(img.colorId) === id && newImages.indexOf(img) !== index
          );

          if (imagesWithColor.length >= 3) {
            ToastError(
              "Er mogen maar 3 afbeeldingen dezelfde kleur hebben",
              "imageVariation"
            );
            return;
          }

          newImages[index].colorId = id;
          newImages[index].sizeId = null; // Clear size ID when setting color
        } else {
          newImages[index].colorId = id;
        }
      }

      // Set the variation index to match the ID
      newImages[index].variationIndex = id;
      newImages[index].changed = true;

      // Make a new array to ensure React detects the change
      setImages([...newImages]);

      // Update product
      if (setUpdatedMedia != null) {
        setUpdatedMedia(true);
      }
    }

    // Delete an image
    function handleDelete(index) {
      if (setUpdatedMedia != null) {
        setUpdatedMedia(true);
      }

      const newImages = [...images];
      newImages.splice(index, 1);
      setImages(newImages);

      // Enable variation switch if no images remain
      if (newImages.length === 0) {
        setSwitchDisabled(false);
      }

      window.isDeleteOrDragEndTriggered = true;
    }

    // Mouse event handlers
    function handleMouseDown(event) {
      if (event.target.matches("select, option, button, span, svg, path")) {
        return;
      } else {
        setMouseDownX(event.clientX);
        setMouseDownY(event.clientY);
      }
    }

    function handleMouseUp(event) {
      if (event.clientX === mouseDownX && event.clientY === mouseDownY) {
        window.isDeleteOrDragEndTriggered = false;
        window.mouseMoved = false;
      } else {
        window.mouseMoved = true;
      }

      setMouseDownX(null);
      setMouseDownY(null);
      handleClick();
    }

    function handleClick() {
      if (!window.isDeleteOrDragEndTriggered && !window.mouseMoved) {
        fileInputRef.current.click();
      }
    }

    // Handle drag and drop reordering
    function handleDragEnd(event) {
      const { active, over } = event;

      if (!over) {
        return;
      }

      if (active.id !== over.id) {
        setImages((images) => {
          const oldIndex = images.findIndex(obj => obj.id === active.id);
          const newIndex = images.findIndex(obj => obj.id === over.id);

          return arrayMove(images, oldIndex, newIndex);
        });

        if (updatedMedia != null) {
          setUpdatedMedia(true);
        }
      }
    }

    // Handle file upload
    function handleFileUpload(e, isDropEvent) {
      e.preventDefault();
      e.stopPropagation();

      const droppedFiles = isDropEvent ? e.dataTransfer.files : e.target.files;
      const newImages = [...images];

      let counter = 0;
      let selectedVariation = selectedColor !== null ? selectedColor : selectedSize;

      // Loop through the dropped files
      for (const uploadedFile of droppedFiles) {
        const imagesWithVariation = newImages.filter(img => {
          return Number(variationSwitch ? img.sizeId : img.colorId) === Number(selectedVariation);
        });

        if (!selectedVariation || imagesWithVariation.length < 3) {
          // Check if image already exists
          const imageExists = images.some(img =>
            img.name === uploadedFile.name ||
            (img.file && img.file.size === uploadedFile.size)
          );

          if (!imageExists) {
            if (product != null) {
              setUpdatedMedia(true);
            }

            let newFileName = uploadedFile.name;
            if (newFileName.includes(",")) {
              newFileName = newFileName.replace(",", "");
            }

            // Check file size and type
            if (
              uploadedFile &&
              uploadedFile.size <= MAX_FILE_SIZE &&
              allowedImageFormats.includes(uploadedFile.type)
            ) {
              const newImage = {
                url: URL.createObjectURL(uploadedFile),
                file: uploadedFile,
                name: createUniqueName(newFileName.replace(/\s/g, "_"), newImages),
                sizeId: variationSwitch && selectedSize ? Number(selectedSize) : null,
                colorId: !variationSwitch && selectedColor ? Number(selectedColor) : null,
                variationIndex: selectedVariation ? Number(selectedVariation) : null,
                id: `${Date.now()}${counter}`,
              };

              newImages.push(newImage);
              counter++;
            } else if (uploadedFile.size >= MAX_FILE_SIZE) {
              // File too large
              ToastWarning(
                `${uploadedFile.name} (${Math.round((uploadedFile.size / 1024 / 1024) * 100) / 100} MB) is te groot! De maximale bestandsgrootte is ${MAX_FILE_SIZE / 1024 / 1024} MB per afbeelding.`
              );

              const newImage = {
                file: { name: newFileName, size: uploadedFile.size },
                name: createUniqueName(newFileName, newImages),
                id: `${Date.now()}${counter}`,
                error: "Afbeelding is te groot",
              };

              newImages.push(newImage);
              counter++;
            } else if (!allowedImageFormats.includes(uploadedFile.type)) {
              // Invalid file type
              ToastWarning(
                `${uploadedFile.name} is niet toegestaan. Bestanden met het type .png, .jpg, .jpeg, .webp, .svg en .gif zijn wel toegestaan.`
              );

              const newImage = {
                file: undefined,
                name: createUniqueName(newFileName.replace(/\s/g, "_"), newImages),
                id: `${Date.now()}${counter}`,
                error: "Ongeldig bestandtype",
              };

              newImages.push(newImage);
              counter++;
            } else {
              ToastError(
                `Er ging iets fout tijdens het uploaden van ${uploadedFile.name}. Probeer het later opnieuw`
              );
            }
          } else {
            ToastError("Deze afbeelding bestaat al", 'already_exists', 1500);
          }
        } else {
          ToastError(
            "Er mogen maar 3 afbeeldingen dezelfde maat/kleur hebben",
            "imageVariation",
            2000
          );
        }
      }


      setImages(newImages);
      if (newImages.length > 0) {
        setSwitchDisabled(true);
      }

      if (!isDropEvent) {
        clearFileInput();
      }
    }

    function clearFileInput() {
      const fileInput = document.getElementById("fileInput");
      if (fileInput) {
        fileInput.value = "";
      }
    }

    function handleDrop(e) {
      handleFileUpload(e, true);
    }

    // Helper functions for file naming
    function fileExists(imageName, imagesArray) {
      return imagesArray.some(image => image.name === imageName);
    }

    function createUniqueName(imageName, imagesArray) {
      const originalName = imageName;
      const lastDotIndex = originalName.lastIndexOf(".");
      const filenameWithoutExt = lastDotIndex > 0 ? originalName.substring(0, lastDotIndex) : originalName;
      const fileExtension = lastDotIndex > 0 ? originalName.substring(lastDotIndex + 1) : "";
      let fileName = fileExtension ? `${filenameWithoutExt}.${fileExtension}` : filenameWithoutExt;
      let fileCount = 0;

      while (fileExists(fileName, imagesArray)) {
        fileCount++;
        fileName = fileExtension
          ? `${filenameWithoutExt}(${fileCount}).${fileExtension}`
          : `${filenameWithoutExt}(${fileCount})`;
      }

      return fileName;
    }

    async function getImages() {
      try {
        setDownloading(true);
        setUploadImgStatus("Foto's aan het ophalen");
        setUploadCount(0);
        setDownloadCount("?");

        const mediaResponse = await middleware.get(
          `products/media/upload?product_id=${product_id}`
        );
        const mediaData = mediaResponse.data.images;
        const newImages = [];
        let selectedType = false;
        if (mediaData.length) {
          selectedType = mediaData.find(img => img.color_id !== null) ? false : true;
        }

        // Process images
        setDownloadCount(mediaData.length);

        for (let i = 0; i < mediaData.length; i++) {
          const imgData = mediaData[i];

          // Get file path and name
          const filePathURL = middleware.defaults.imagesBaseURL + imgData.path;
          const lastSeparatorIndex = filePathURL.lastIndexOf("/");
          const fileName = filePathURL.substring(lastSeparatorIndex + 1);

          const blob = base64ToBlob(imgData.blob);
          const newImage = {
            url: URL.createObjectURL(blob),
            file: blob,
            name: createUniqueName(fileName, newImages),
            colorId: imgData.color_id,
            sizeId: imgData.size_id,
            variationIndex: variationSwitch ? imgData.size_id : imgData.color_id,
            id: `${Date.now()}${i + 1}`,
          };

          newImages[i] = newImage;
          setUploadCount(prev => prev + 1);
        }

        setDownloading(false);
        setImages(newImages);

        // Disable variation switch if images exist
        if (newImages.length > 0) {
          setSwitchDisabled(true);
        }
      } catch (error) {
        console.warn("Error fetching images:", error);
        setDownloading(false);
      }
    }
  }
);

export default MediaUploaderComponent;